import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "../config/axios";
import NoPurchaseBanner from "../common-components/NoPurchaseBanner";
import HomeFooter from "../common-components/HomeFooter";
import Header from "./dashboard/Header";
import { submitWireTransferRequest } from "../actions/wireTransfer";
import { toastAlert } from "../lib/toastAlert";
import isLogin from "../lib/isLogin";
import {
  getGoldCoinName,
  getSweepsCoinName,
} from "../../utility/complianceCopy";
import "../../style/stage9.css";

const WIRE_MIN_USD = 500;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  amount: "",
  note: "",
};

export default function Storefront() {
  const history = useHistory();
  const account = useSelector((state) => state.account);
  const loggedIn = Boolean(account?.emailId && isLogin());
  const goldName = getGoldCoinName();
  const sweepsName = getSweepsCoinName();

  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [packsError, setPacksError] = useState("");

  const [showWireForm, setShowWireForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPacksLoading(true);
    axios
      .get("/api/coin-packs")
      .then((r) => {
        if (cancelled) return;
        setPacks(Array.isArray(r.data?.result) ? r.data.result : []);
        setPacksError("");
      })
      .catch(() => {
        if (cancelled) return;
        setPacks([]);
        setPacksError("Coin packs are temporarily unavailable.");
      })
      .finally(() => {
        if (!cancelled) setPacksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showWireForm) return;
    setForm((prev) => ({
      ...prev,
      name: prev.name || account?.name || account?.userName || "",
      email: prev.email || account?.emailId || "",
      phone: prev.phone || account?.phoneNo || account?.phone || "",
    }));
  }, [showWireForm, account]);

  const onPackContinue = (pack) => {
    toastAlert(
      "info",
      "Wire transfer is not integrated yet. It will be available in a future update.",
      `pack-checkout-${pack.packId}-${Date.now()}`
    );
  };

  const openWireForm = () => {
    if (!loggedIn) {
      toastAlert(
        "error",
        "Please log in to request a wire transfer representative.",
        "store-login"
      );
      history.push(`/login?redirect=${encodeURIComponent("/storefront")}`);
      return;
    }
    setErrors({});
    setShowWireForm(true);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const clientValidate = () => {
    const next = {};
    if (!String(form.name).trim()) next.name = "Name is required";
    if (!String(form.email).trim()) next.email = "Email is required";
    if (!String(form.phone).trim()) next.phone = "Phone is required";
    const amount = Number(form.amount);
    if (!Number.isFinite(amount)) next.amount = "Enter a desired wire amount";
    else if (amount < WIRE_MIN_USD) {
      next.amount = `Minimum wire amount is $${WIRE_MIN_USD}`;
    }
    return next;
  };

  const onSubmitWire = async (e) => {
    e.preventDefault();
    const next = clientValidate();
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSubmitting(true);
    const res = await submitWireTransferRequest({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      amount: Number(form.amount),
      note: form.note.trim(),
    });
    setSubmitting(false);

    if (res.httpStatus === 401) {
      toastAlert("error", "Please log in to continue.", "store");
      history.push(`/login?redirect=${encodeURIComponent("/storefront")}`);
      return;
    }

    if (res.status) {
      setForm(emptyForm);
      setShowWireForm(false);
      toastAlert(
        "success",
        res.message ||
          "Request submitted. A representative will contact you about your wire transfer.",
        `wire-submitted-${Date.now()}`
      );
      return;
    }

    if (res.errors && typeof res.errors === "object") {
      setErrors(res.errors);
    }
    toastAlert("error", res.message || "Could not submit request", "store");
  };

  return (
    <>
      <Header />
      <main className="container dts-storefront">
        <NoPurchaseBanner />
        <h1>Get {goldName}</h1>
        <p className="dts-stage9-lead">
          {goldName} are for entertainment play. Each pack includes free{" "}
          {sweepsName}. Wire transfer is not integrated yet. For ${WIRE_MIN_USD}{" "}
          or more, you can request a personal representative.
        </p>

        {packsError ? (
          <div role="alert" className="alert alert-warning">
            {packsError}
          </div>
        ) : null}
        {packsLoading ? <p role="status">Loading coin packs…</p> : null}
        {!packsLoading && !packsError && !packs.length ? (
          <p role="status">No coin packs are available right now.</p>
        ) : null}

        <div className="storefront__grid dts-pack-grid" role="list">
          {packs.map((pack) => (
            <article key={pack.packId} role="listitem" className="storefront__pack">
              <div className="storefront__pack-gc">
                {Number(pack.gc_balance).toLocaleString()}
                <span className="storefront__pack-gc-label">{goldName}</span>
              </div>
              <div className="storefront__pack-bonus">
                + {Number(pack.bonusUnplayedDc).toLocaleString()} free {sweepsName}
              </div>
              <div className="storefront__pack-price">
                ${Number(pack.priceUsd).toFixed(2)}
              </div>
              <button
                type="button"
                className="btn storefront__btn-primary"
                onClick={() => onPackContinue(pack)}
              >
                Continue
              </button>
            </article>
          ))}
        </div>

        <section className="dts-wire-section" aria-labelledby="wire-rep-heading">
          <article className="dts-wire-card">
            <div className="dts-wire-card__body">
              <p className="dts-redemption-kicker">Wire transfer</p>
              <h2 id="wire-rep-heading" className="dts-wire-card__title">
                Request a personal representative
              </h2>
              <p className="dts-wire-card__copy">
                For purchases of ${WIRE_MIN_USD} or more. A representative will
                contact you to arrange the wire transfer. Sending this request
                does not charge you.
              </p>
            </div>
            <button
              type="button"
              className="btn storefront__btn-primary dts-wire-card__cta"
              onClick={openWireForm}
            >
              Request a representative
            </button>
          </article>
        </section>
      </main>

      {/* Wire transfer request form */}
      <Modal
        show={showWireForm}
        onHide={() => !submitting && setShowWireForm(false)}
        centered
        backdrop="static"
        className="dts-wire-modal"
        dialogClassName="dts-wire-modal__dialog"
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Wire transfer request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <form onSubmit={onSubmitWire} noValidate>
              <p className="dts-wire-modal__lead">
                Minimum ${WIRE_MIN_USD} USD. Sending this request does not
                charge your card or wallet. A representative will contact you
                to arrange the wire transfer.
              </p>
              <div className="dts-wire-field">
                <label className="form-label" htmlFor="wire-name">
                  Full name
                </label>
                <input
                  id="wire-name"
                  name="name"
                  className="form-control"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your full name"
                  autoComplete="name"
                  required
                />
                {errors.name ? (
                  <div className="invalid-feedback d-block">{errors.name}</div>
                ) : null}
              </div>
              <div className="dts-wire-field">
                <label className="form-label" htmlFor="wire-email">
                  Email
                </label>
                <input
                  id="wire-email"
                  name="email"
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
                {errors.email ? (
                  <div className="invalid-feedback d-block">{errors.email}</div>
                ) : null}
              </div>
              <div className="dts-wire-fields">
                <div className="dts-wire-field">
                  <label className="form-label" htmlFor="wire-phone">
                    Phone
                  </label>
                  <input
                    id="wire-phone"
                    name="phone"
                    type="tel"
                    className="form-control"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="Your phone number"
                    autoComplete="tel"
                    required
                  />
                  {errors.phone ? (
                    <div className="invalid-feedback d-block">{errors.phone}</div>
                  ) : null}
                </div>
                <div className="dts-wire-field">
                  <label className="form-label" htmlFor="wire-amount">
                    Amount (USD)
                  </label>
                  <input
                    id="wire-amount"
                    name="amount"
                    type="number"
                    min={WIRE_MIN_USD}
                    step="0.01"
                    className="form-control"
                    value={form.amount}
                    onChange={onChange}
                    placeholder={`${WIRE_MIN_USD} or more`}
                    required
                  />
                  {errors.amount ? (
                    <div className="invalid-feedback d-block">{errors.amount}</div>
                  ) : null}
                </div>
              </div>
              <div className="dts-wire-field">
                <label className="form-label" htmlFor="wire-note">
                  Note (optional)
                </label>
                <textarea
                  id="wire-note"
                  name="note"
                  className="form-control"
                  rows={3}
                  value={form.note}
                  onChange={onChange}
                  placeholder="Anything the representative should know"
                  maxLength={2000}
                />
                {errors.note ? (
                  <div className="invalid-feedback d-block">{errors.note}</div>
                ) : null}
              </div>
              <div className="dts-wire-modal__actions">
                <button
                  type="button"
                  className="btn dts-wire-modal__cancel"
                  onClick={() => setShowWireForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn dts-wire-modal__submit"
                  disabled={submitting}
                >
                  {submitting ? "Submitting…" : "Submit request"}
                </button>
              </div>
            </form>
        </Modal.Body>
      </Modal>

      <HomeFooter />
    </>
  );
}
